import React from 'react';
import BaseStep from './BaseStep';
import Filters from '../../../Filters/Filters';
import Filter from '../../../Filter/Filter';
import Loader from '../api/Loader';
import { AssignmentTable } from './Assignment/AssignmentTable';
import ActionBar, { ActionButton } from '../components/ActionBar';
import MuiIcons from '../utils/MuiIcons';
import UrlHelper from '../utils/UrlHelper';
import UIHelper from '../utils/UIHelper';

/**
 * @typedef {import('../types/Assignments').Assignment} Assignment
 */
export default class AssignmentStep extends BaseStep {
  static id = 'assignment-step';

  static title = 'Assignment selection';

  /**
   * Triggers data loading for this step.
   * @see super.wizardData.api_allAssignments
   */
  static triggerDataLoad() {
    Loader.loadCurrentUser()
      .then(user => {
        super.wizardData.api_user = user;
        Loader.loadAllAssignments(super.wizardData.targetCampaign?.campaignId, user.userLocation)
          .then(assignments => {
            super.wizardData.api_allAssignments = assignments;
            super.wizardData.api_filteredAssignments = assignments;
          })
          .catch(error => {
            super.notification.error('Network error while loading assignments data', error);
          });
      })
      .catch(error => {
        super.notification.error('Unable to load current User data', error);
      });
  }

  static #triggerSearchUpdateCounter = 0;

  /**
   * Triggers a search update to reload assignments based on the new search query.
   * @param {string} search - The URL search query.
   * @see super.wizardData.api_filteredAssignments
   */
  static triggerSearchUpdate = search => {
    // Increment the counter to indicate a new search update has been triggered
    AssignmentStep.#triggerSearchUpdateCounter += 1;

    // Capture the current counter value to check against when the promise resolves
    const currentCount = AssignmentStep.#triggerSearchUpdateCounter;

    Loader.loadAssignments(
      search,
      super.wizardData.targetCampaign?.campaignId,
      super.wizardData.api_user?.userLocation || undefined,
    )
      .then(assignments => {
        if (currentCount !== AssignmentStep.#triggerSearchUpdateCounter) {
          // A newer search update has been triggered, so we ignore this result
          return;
        }

        super.wizardData.api_filteredAssignments = assignments;
      })
      .catch(error => {
        super.notification.error('Network error while loading assignments for search', error);
      });
  };

  /**
   * Generates the filters component for this step based on the facet fields of the filtered assignments data.
   * @returns {JSX.Element|null} The FiltersContainer component or null if no facet fields.
   */
  static getFilters = () => {
    const assignments = super.wizardData.api_filteredAssignments;

    if (!assignments) {
      return null;
    }

    const facet_fields = assignments.facet_fields;

    return (
      <Filters>
        <Filter
          type="text"
          name="search"
          title="Search"
          placeholder="Search locations and devices"
        />
        <Filter type="preset" preset="depth" name="folderDepth" title="Include sublocation" />
        <Filter type="checkbox" name="type" title="Type" data={facet_fields.type} />
        <Filter
          type="checkbox"
          name="content_type"
          title="Content Type"
          data={facet_fields.content_type}
        />
        <Filter type="tag" name="tags" title="Tags" data={facet_fields.tags} />
      </Filters>
    );
  };

  /**
   * Determines if this step has a filter button in the actions menu.
   * To be overridden by subclasses if needed.
   * @returns {boolean} True if has filter button, false otherwise.
   */
  static hasFilter() {
    return true;
  }

  /**
   * Indicates whether the data for this step is still loading.
   * @returns {boolean} True if data is loading, false otherwise.
   */
  static dataIsLoading() {
    return (
      AssignmentStep.isFilteredAssignmentsWaitingForLoad() ||
      AssignmentStep.isAllAssignmentWaitingForLoad()
    );
  }

  /**
   * Checks if the filtered assignments data is still loading.
   * @returns {boolean} True if filtered assignments are waiting for load, false otherwise.
   */
  static isFilteredAssignmentsWaitingForLoad() {
    return !super.wizardData.api_filteredAssignments;
  }

  /**
   * Checks if the all assignments data is still loading.
   * @returns {boolean} True if all assignments are waiting for load, false otherwise.
   */
  static isAllAssignmentWaitingForLoad() {
    return !super.wizardData.api_allAssignments;
  }

  /**
   * Determines if the wizard can proceed from this step.
   * To be overridden by subclasses if needed.
   * @param {WizardData} wizardData - The wizard data object to check for validation.
   * @returns {boolean} True if can proceed, false otherwise.
   */
  static canProceed() {
    return super.wizardData.selectedDeviceIds.length > 0;
  }

  // Object methods BELOW -----------------------------------------------------

  /**
   * UserLocation that needs to bound user working with items
   * @return {string}
   */
  getUserLocation() {
    return super.wizardData.api_user?.userLocation || '/';
  }

  /**
   * Handles toggling the selection of an assignment when the user interacts with it in the UI.
   * @param {Assignment} assignment - The assignment to toggle.
   * @see this.wizardData.toggleAssignment
   */
  handleToggleSelection = assignment => {
    this.wizardData.toggleAssignment(assignment);
  };

  /**
   * Gets the current search query from the URL to use for loading filtered assignments.
   * @returns {string} The current search query from the URL.
   */
  getLocationSearch = () => {
    return this?.history?.location?.search || '';
  };

  /**
   * Checks if the current parent location in the URL is at the root level.
   * @returns {boolean} True if parent is at root level, false otherwise.
   */
  isParentAtRootLevel = () => {
    const parent = UrlHelper.getUrlParam(
      this.getLocationSearch(),
      'parent',
      this.getUserLocation(),
    );

    return (
      UIHelper.location.normalisePath(parent) ===
      UIHelper.location.normalisePath(this.getUserLocation())
    );
  };

  /**
   * Checks if the folder depth filter is set in the URL,
   * indicating whether to include sublocations in the assignment search.
   * @returns {boolean} True if folder depth is set, false otherwise.
   */
  isFolderDepthSet = () => {
    return UrlHelper.getUrlParam(this.getLocationSearch(), 'parent', '').endsWith('/*');
  };

  /**
   * Sets a new parent value in the URL to navigate into the location.
   * @param {string} value - The new parent value to set in the URL.
   */
  setNewUrlParentValue = value => {
    const newSearch = UrlHelper.setUrlParam(this.getLocationSearch(), 'parent', value);
    const folderDepthValue = this.isFolderDepthSet() ? '*' : '';
    const newUrl = `${this.history.location.pathname}${newSearch}${folderDepthValue}`;

    if (newUrl.includes('//')) {
      console.error('Error: URL has double slashes which may cause navigation issues:', newUrl);
    }

    if (newUrl.includes('??')) {
      console.error(
        'Error: URL has double question marks which may cause navigation issues:',
        newUrl,
      );
    }

    this.history.push(newUrl);
  };

  /**
   * Calculates the URL for navigating up one parent level based on the current parent location in the URL.
   * @returns {string} The URL for navigating up one parent level.
   */
  upAParentLevel = () => {
    if (this.isParentAtRootLevel()) {
      return this.getUserLocation();
    }

    const parent = UrlHelper.getUrlParam(this.getLocationSearch(), 'parent');

    return `${parent.split('/').slice(0, -2).join('/')}/`;
  };

  /**
   * Generates the action buttons for the action bar, including navigation buttons to go back to root or one level up.
   * @returns {ActionButton[]} An array of Button instances for the action bar.
   */
  getActionButtons() {
    return [
      new ActionButton(
        '',
        MuiIcons.icons.arrow_upward,
        () => this.setNewUrlParentValue(this.getUserLocation()),
        {
          disabled: this.isParentAtRootLevel(),
          tooltip: 'Up to root',
          dataTest: 'assignment-up-to-root-button',
        },
      ),
      new ActionButton(
        '',
        MuiIcons.icons.arrow_back,
        () => this.setNewUrlParentValue(this.upAParentLevel()),
        {
          disabled: this.isParentAtRootLevel(),
          tooltip: 'Back one level',
          dataTest: 'assignment-back-one-level-button',
        },
      ),
    ];
  }

  /**
   * Handles the click event on the search button, triggering a search update with the current search query.
   * @see super.triggerSearchUpdate
   */
  clickSearch = () => {
    // press enter on input to trigger search update
    document
      .getElementById('assignment-text-input')
      ?.querySelector('input')
      ?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter' }));
  };

  /**
   * Renders the search input component for filtering assignments, including a text input and a search button.
   * @returns {JSX.Element} The search input component.
   */
  getSearchInput() {
    return (
      <div className="assignment-search">
        <span id="assignment-checkbox-input">
          <Filter type="preset" preset="depth" name="folderDepth" title="Include sublocation" />
        </span>
        <span id="assignment-text-input">
          <Filter
            type="text"
            name="search"
            title="Search"
            placeholder="Search locations and devices"
          />
        </span>
        <button type="button" onClick={this.clickSearch}>
          Search
        </button>
      </div>
    );
  }

  /**
   * Renders the step component.
   * @returns {JSX.Element} The step component.
   */
  render() {
    return (
      <>
        {this.renderNavigationPrompt(true)}
        <div className="assignment-step">
          <h2>Assignment to devices or locations</h2>
          <div className="action-bar-container">
            <ActionBar buttons={this.getActionButtons()} />
            {this.getSearchInput()}
          </div>
          <AssignmentTable
            assignmentsList={this.wizardData?.api_filteredAssignments?.assignments || []}
            wizardData={this.wizardData}
            onToggle={this.handleToggleSelection}
            changeParentHandler={this.setNewUrlParentValue}
          />
        </div>
      </>
    );
  }
}
